import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './App.css';

interface NFT {
  _id: string;
  name: string;
  isActive: boolean;
  currentLowestPrice: string | null;
  lastUpdated: string;
}

interface PriceHistory {
  _id: string;
  nftName: string;
  price: string;
  tokenId: string;
  timestamp: string;
}

const API_BASE_URL = 'https://api.ordsat.xyz/api';

function App() {
  const [nfts, setNfts] = useState<NFT[]>([]);
  const [newNftName, setNewNftName] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedNft, setSelectedNft] = useState<string | null>(null);
  const [priceHistory, setPriceHistory] = useState<PriceHistory[]>([]);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | 'name'>('asc');

  useEffect(() => {
    fetchNfts();
  }, []);

  const fetchNfts = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/nfts`);
      setNfts(response.data);
    } catch (error) {
      console.error('Error fetching NFTs:', error);
    }
  };

  const addNft = async () => {
    if (!newNftName.trim()) return;

    setLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/nfts`, { name: newNftName });
      setNewNftName('');
      fetchNfts();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Error adding NFT');
    } finally {
      setLoading(false);
    }
  };

  const removeNft = async (id: string) => {
    try {
      await axios.delete(`${API_BASE_URL}/nfts/${id}`);
      fetchNfts();
    } catch (error) {
      console.error('Error removing NFT:', error);
    }
  };

  const toggleNft = async (id: string) => {
    try {
      await axios.patch(`${API_BASE_URL}/nfts/${id}/toggle`);
      fetchNfts();
    } catch (error) {
      console.error('Error toggling NFT:', error);
    }
  };

  const checkPrice = async (name: string) => {
    setLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/nfts/${name}/check-price`);
      fetchNfts();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Error checking price');
    } finally {
      setLoading(false);
    }
  };

  const fetchPriceHistory = async (name: string) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/nfts/${name}/history`);
      setPriceHistory(response.data);
      setSelectedNft(name);
    } catch (error) {
      console.error('Error fetching price history:', error);
    }
  };

  const formatPrice = (priceWei: string) => {
    const price = parseFloat(priceWei) / Math.pow(10, 18);
    return price.toLocaleString(undefined, { maximumFractionDigits: 4 });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getSortedNfts = () => {
    const sortedNfts = [...nfts];

    if (sortOrder === 'name') {
      return sortedNfts.sort((a, b) => a.name.localeCompare(b.name));
    }

    return sortedNfts.sort((a, b) => {
      const priceA = a.currentLowestPrice ? parseFloat(a.currentLowestPrice) : Infinity;
      const priceB = b.currentLowestPrice ? parseFloat(b.currentLowestPrice) : Infinity;

      if (sortOrder === 'asc') {
        return priceA - priceB;
      } else {
        return priceB - priceA;
      }
    });
  };

  return (
    <div className="App">
      <header className="App-header">
        <h1>MSU NFT Price Monitor</h1>

        <div className="add-nft-section">
          <input
            type="text"
            value={newNftName}
            onChange={(e) => setNewNftName(e.target.value)}
            placeholder="Enter NFT name (e.g., Black Cat Camper)"
          />
          <button
            onClick={addNft}
            disabled={loading}
          >
            {loading ? 'Adding...' : 'Add NFT'}
          </button>
        </div>

        <div className="nft-list">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h2>Monitored NFTs ({nfts.length})</h2>
            <div>
              <label style={{ marginRight: '10px', fontSize: '14px' }}>Sort by:</label>
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc' | 'name')}
                style={{ padding: '6px', fontSize: '14px', border: '1px solid #ced4da', borderRadius: '4px' }}
              >
                <option value="asc">Price: Low to High</option>
                <option value="desc">Price: High to Low</option>
                <option value="name">Name: A to Z</option>
              </select>
            </div>
          </div>

          {nfts.length === 0 ? (
            <p>No NFTs being monitored. Add one above!</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                backgroundColor: 'white',
                borderRadius: '8px',
                overflow: 'hidden',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontSize: '14px', fontWeight: '600' }}>Name</th>
                    <th style={{ padding: '8px', textAlign: 'center', borderBottom: '2px solid #dee2e6', fontSize: '14px', fontWeight: '600' }}>Status</th>
                    <th style={{ padding: '8px', textAlign: 'right', borderBottom: '2px solid #dee2e6', fontSize: '14px', fontWeight: '600' }}>Current Price</th>
                    <th style={{ padding: '8px', textAlign: 'center', borderBottom: '2px solid #dee2e6', fontSize: '14px', fontWeight: '600' }}>Last Updated</th>
                    <th style={{ padding: '8px', textAlign: 'center', borderBottom: '2px solid #dee2e6', fontSize: '14px', fontWeight: '600' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {getSortedNfts().map((nft, index) => (
                    <tr
                      key={nft._id}
                      style={{
                        backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8f9fa',
                        borderBottom: '1px solid #dee2e6'
                      }}
                    >
                      <td style={{ padding: '8px', fontSize: '14px', color: '#2c3e50' }}>
                        {nft.name}
                      </td>
                      <td style={{ padding: '8px', textAlign: 'center' }}>
                        <span style={{
                          padding: '2px 6px',
                          borderRadius: '3px',
                          fontSize: '11px',
                          fontWeight: '600',
                          backgroundColor: nft.isActive ? '#d4edda' : '#f8d7da',
                          color: nft.isActive ? '#155724' : '#721c24'
                        }}>
                          {nft.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td style={{ padding: '8px', textAlign: 'right', fontFamily: 'monospace', fontSize: '14px' }}>
                        {nft.currentLowestPrice ? (
                          <span style={{
                            fontWeight: '600',
                            color: '#2c3e50'
                          }}>
                            {formatPrice(nft.currentLowestPrice)} tokens
                          </span>
                        ) : (
                          <span style={{ color: '#6c757d', fontStyle: 'italic' }}>N/A</span>
                        )}
                      </td>
                      <td style={{ padding: '8px', textAlign: 'center', fontSize: '12px', color: '#6c757d' }}>
                        {formatDate(nft.lastUpdated)}
                      </td>
                      <td style={{ padding: '8px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '3px', justifyContent: 'center', flexWrap: 'wrap' }}>
                          <button
                            onClick={() => toggleNft(nft._id)}
                            style={{
                              padding: '3px 6px',
                              fontSize: '11px',
                              border: 'none',
                              borderRadius: '3px',
                              backgroundColor: nft.isActive ? '#ffc107' : '#28a745',
                              color: 'white',
                              cursor: 'pointer'
                            }}
                          >
                            {nft.isActive ? 'Pause' : 'Resume'}
                          </button>
                          <button
                            onClick={() => checkPrice(nft.name)}
                            disabled={loading}
                            style={{
                              padding: '3px 6px',
                              fontSize: '11px',
                              border: 'none',
                              borderRadius: '3px',
                              backgroundColor: '#007bff',
                              color: 'white',
                              cursor: loading ? 'not-allowed' : 'pointer',
                              opacity: loading ? 0.6 : 1
                            }}
                          >
                            Check
                          </button>
                          <button
                            onClick={() => fetchPriceHistory(nft.name)}
                            style={{
                              padding: '3px 6px',
                              fontSize: '11px',
                              border: 'none',
                              borderRadius: '3px',
                              backgroundColor: '#17a2b8',
                              color: 'white',
                              cursor: 'pointer'
                            }}
                          >
                            History
                          </button>
                          <button
                            onClick={() => removeNft(nft._id)}
                            style={{
                              padding: '3px 6px',
                              fontSize: '11px',
                              border: 'none',
                              borderRadius: '3px',
                              backgroundColor: '#dc3545',
                              color: 'white',
                              cursor: 'pointer'
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {selectedNft && priceHistory.length > 0 && (
          <div className="price-history">
            <h3>Price History for {selectedNft}</h3>
            <button onClick={() => setSelectedNft(null)}>Close</button>
            <div style={{ maxHeight: '300px', overflowY: 'auto', marginTop: '10px' }}>
              {priceHistory.map((history) => (
                <div key={history._id} style={{
                  border: '1px solid #ddd',
                  margin: '5px',
                  padding: '10px',
                  backgroundColor: '#f9f9f9'
                }}>
                  <p><strong>Price:</strong> {formatPrice(history.price)} tokens</p>
                  <p><strong>Token ID:</strong> {history.tokenId}</p>
                  <p><strong>Time:</strong> {formatDate(history.timestamp)}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </header>
    </div>
  );
}

export default App;
